<template>
	<PaymentClient
		:api-config="apiConfig"
		:order="order"
		:customer="customer"
		:client-config="clientConfig"
		:enabled="enabled"
		:debug="debug"
		action="change-payment-method"
		integration="stripe"
		v-slot="{ paymentClients }"
		@loading="$emit('loading', $event)"
		@ready="$emit('ready', $event)"
		@submit="$emit('submit', $event)"
		@cancelled="$emit('cancelled', $event)"
		@success="$emit('success', $event)"
		@error="onError"
	>
		<slot :payment-clients="paymentClients"></slot>
	</PaymentClient>
</template>

<script setup lang="ts">
// Kova wrapper around web-billing's PaymentClient, fixed to the payment-method-change
// action. The widget owns the whole backend conversation (POST /v1/client-session on
// mount; at submit POST /v1/customers best-effort then /v1/payment-method/change; 3DS
// via /v1/payment/confirm) over the anonymous `billingWidgetAxios`; this component only
// assembles order/customer from the stores and re-emits the widget events. Ported from med-web-funnel's
// src/modules/WebBilling/components/BillingClient.vue, minus the funnel-only pieces
// (error popups, vault manager, Affirm) — our flow has its own /payment/failed screen.
import '@simple-life-app/web-billing/dist/style.css'
import { computed } from 'vue'
import * as Sentry from '@sentry/vue'
import { PaymentClient } from '@simple-life-app/web-billing'
import type {
	Customer,
	ICancelledEvent,
	IErrorEvent,
	ILoadingEvent,
	IReadyEvent,
	ISubmitEvent,
	ISuccessEvent,
	Order,
} from '@simple-life-app/web-billing'
import { billingWidgetAxios } from '@/api/medBilling'
import { config } from '@/config'
import { useAuthStore } from '@/store/auth'
import { useGeoStore } from '@/store/geo'
import { useSubscriptionStore } from '@/store/subscription'

withDefaults(defineProps<{ enabled?: boolean }>(), { enabled: true })

const emit = defineEmits<{
	(e: 'loading', payload: ILoadingEvent): void
	(e: 'ready', payload: IReadyEvent): void
	(e: 'submit', payload: ISubmitEvent): void
	(e: 'cancelled', payload: ICancelledEvent): void
	(e: 'success', payload: ISuccessEvent): void
	(e: 'error', payload: IErrorEvent): void
}>()
// NOTE: PaymentClient also declares `initialized` and `validated` events, but the
// vendored 1.15.0 never actually emits them (verified in dist/billing.es.js) — they
// are deliberately not part of this wrapper's API.

const auth = useAuthStore()
const geo = useGeoStore()
const subscriptionStore = useSubscriptionStore()

// Widget console logging on non-prod builds only (funnel parity: ENV_DEV_OR_STAGING).
// MODE, not DEV: `vite build --mode development` keeps NODE_ENV=production, so a DEV
// gate would silence the widget's debug events on the deployed dev stand.
const debug = import.meta.env.MODE !== 'production'

const apiConfig = { axiosInstance: billingWidgetAxios }

/**
 * Order amount in minor units, consumed ONLY client-side by Stripe's Express Checkout
 * Element — without it the Apple/Google Pay buttons never mount. It is not sent to
 * /v1/payment-method/change; the backend derives the real hold from its technical
 * product ($1 verification charge per the PRD), which this constant mirrors.
 */
const VERIFICATION_HOLD_AMOUNT_MINOR = 100

const currency = computed(() => subscriptionStore.getCurrency?.toUpperCase?.() ?? 'USD')

const order = computed<Order>(() => ({
	product_id: config.billingChangeProductId,
	country_code: geo.country ?? 'US',
	currency: currency.value,
	// Kova billing is Stripe-only; client-session defaults to primer when omitted.
	provider: 'stripe',
	amount: VERIFICATION_HOLD_AMOUNT_MINOR,
	deferred_intent: true,
	metadata: {
		google_pay: 1,
		paypal: 0,
		paypal_native: 0,
	},
}))

interface Auth0UserShape {
	email?: string
}

const customer = computed<Customer>(() => ({
	// Billing keys the customer on the med-users signup-session id, which the
	// subscription DTO exposes as customer_id (the parent view gates on its presence).
	customer_id: subscriptionStore.subscription?.customer_id ?? '',
	subscription_id: subscriptionStore.subscription?.subscription_id,
	email: auth.profile?.email ?? (auth.user as Auth0UserShape | null)?.email ?? '',
	user_agent: navigator.userAgent,
	customer_geo_location: {
		continent_code: geo.continent ?? null,
		country_code: geo.country ?? null,
		region: geo.region ?? null,
		city: geo.city ?? null,
		postal_code: geo.postalCode ?? null,
		timezone: Intl?.DateTimeFormat?.().resolvedOptions?.()?.timeZone ?? null,
	},
}))

const clientConfig = {
	locale: 'en',
	card: {
		cardholderName: { required: false },
		postalCode: { required: false },
	},
}

function onError(payload: IErrorEvent): void {
	const error = payload?.error as
		| (Error & { response?: { status?: number; data?: Record<string, unknown> } })
		| undefined
	const responseData = error?.response?.data
	const reasonCode = (responseData?.reason_code ?? responseData?.error_code ?? 'unknown') as string
	// Routine client-side card validation (Confirm tapped with incomplete fields —
	// source 'sdk', no HTTP response) is normal UX, not an incident: keep it visible
	// but at warning level and under its own reason code so it doesn't drown real
	// decline monitoring.
	const isClientCardValidation = payload.paymentMethod === 'card' && payload.source === 'sdk' && !error?.response

	Sentry.withScope((scope) => {
		scope.setLevel(isClientCardValidation ? 'warning' : 'error')
		scope.setTags({
			flow: 'billing',
			paymentErrorProvider: payload.integration,
			paymentErrorMethod: payload.paymentMethod,
			paymentErrorFrom: 'payment_method_change',
			paymentErrorReasonCode: isClientCardValidation ? 'client_validation' : reasonCode,
			...(error?.response?.status != null ? { paymentErrorStatus: error.response.status } : {}),
		})
		// Capture even when there is no Error object, so a malformed payload from the
		// payment widget is never silently dropped.
		if (error instanceof Error) {
			scope.captureException(error)
		} else {
			scope.captureMessage(`payment_error: ${payload.integration || 'unknown'} ${reasonCode}`)
		}
	})

	emit('error', payload)
}
</script>

<style scoped>
/* The Stripe 3DS challenge iframe must beat every app overlay (incl. the verifying screen). */
:deep(.PrimerCheckout__scene--3dsModal) {
	z-index: 10000 !important;
}
</style>
